import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';
import { environment } from 'src/environments/environment';
import { CognitoService, IUser } from '../../services/cognito.service';
import { SpinnerService } from 'src/app/shared/services/spinner.service';
import { loadUser } from '../../auth.actions';
import { Store } from '@ngrx/store';
import { User } from 'src/app/shared/models/user.model';
import { AppState } from 'src/app/app.reducer';
import { FirebaseApp } from '@angular/fire/app';
import { getMessaging, getToken } from '@angular/fire/messaging';
import { RegisteredDevicesService } from 'src/app/shared/services/registered-devices.service';
import { UserService } from 'src/app/dashboard/services/user.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { SetupMfaComponent } from 'src/app/dashboard/components/user-profile/setup-mfa/setup-mfa.component';
@Component({
  selector: 'app-sign-in-ac',
  templateUrl: './sign-in-ac.component.html',
  styleUrls: ['./sign-in-ac.component.sass'],
})
export class SignInAcComponent implements OnInit {
  loading: boolean;
  user: IUser;
  unregisteredMail: boolean = false;
  loginForm: FormGroup;
  loginError: boolean = false;
  showPassword: boolean = false;
  token: string | undefined;

  constructor(
    private userService: UserService,
    private registeredDevice: RegisteredDevicesService,
    private setupMFaDialog: MatDialog,
    private afApp: FirebaseApp,
    private store: Store<AppState>,
    private router: Router,
    private cognitoService: CognitoService,
    private fb: FormBuilder,
    private spinner: SpinnerService,
  ) {
    this.loginForm = this.fb.group({
      email: new FormControl('', [
        Validators.required,
        Validators.email,
        this.noSpacesValidator,
      ]),
      password: new FormControl('', [Validators.required]),
    });
    this.loading = false;
    this.user = {} as IUser;
  }
  ngOnInit(): void {}

  public onSubmit(form: FormGroup) {
    this.user = {
      email: form.get('email')?.value,
      password: form.get('password')?.value,
      showPassword: false,
      code: '',
      name: '',
    };
    this.signIn();
  }

  requestPermission() {
    const messaging = getMessaging(this.afApp);
    getToken(messaging, { vapidKey: environment.firebase.applicationServerKey })
      .then((currentToken) => {
        if (currentToken) {
          this.token = currentToken;
        }
      })
      .catch(() => {});
  }

  /** Validador personalizado: No permite espacios en el correo */
  noSpacesValidator(control: AbstractControl): ValidationErrors | null {
    return control.value && control.value.includes(' ')
      ? { noSpaces: true }
      : null;
  }

  /** Evita que los usuarios peguen espacios en el campo */
  handlePaste(event: ClipboardEvent) {
    event.preventDefault();
    const text = event.clipboardData?.getData('text') || '';
    this.loginForm.get('email')?.setValue(text.replace(/\s/g, ''));
  }

  public async signIn(): Promise<void> {
    this.unregisteredMail = false;
    this.loginError = false;
    const ref = this.spinner.start();

    try {
      // Inicia sesión con Cognito
      await this.cognitoService.signIn(this.user.email, this.user.password);

      // Obtiene la sesión y el usuario autenticado en paralelo
      const [cognitoSession, cognitoUser] = await Promise.all([
        this.cognitoService.getSession(),
        this.cognitoService.getAuthUser(),
      ]);

      const userGroup =
        cognitoSession?.accessToken?.payload?.['cognito:groups']?.[0];
      const userRole = cognitoUser?.attributes?.['custom:user-group'];

      if (!userGroup || !userRole) {
        throw new Error('❌ No se pudo obtener el grupo o rol del usuario');
      }

      // Actualiza el store con el usuario y su rol
      this.store.dispatch(
        loadUser({ email: this.user.email, role: userGroup }),
      );

      this.loginError = false;

      // Mostrar modal para configurar MFA si aplica
      if (
        cognitoUser?.preferredMFA === 'NOMFA' &&
        userRole === 'aguacate_operator'
      ) {
        const dialogConfig: MatDialogConfig = {
          disableClose: false,
          autoFocus: true,
          maxHeight: '90vh',
          maxWidth: '100vw',
        };
        this.setupMFaDialog.open(SetupMfaComponent, dialogConfig);
      }

      if (
        userRole === 'aguacate_retail_client' ||
        userRole === 'aguacate_retail_cashier'
      ) {
        this.router.navigate(['/retail/home']);
      } else {
        this.router.navigate(['/wallet']);
      }
    } catch (error) {
      this.loginError = true;
      this.loading = false;
    } finally {
      this.spinner.stop(ref);
    }
  }
}
